import {
  ChevronDownIcon,
  PlusSmallIcon as PlusSmIcon,
} from "@heroicons/react/24/solid";
import { Fragment, useState } from "react";
import { Menu, Dialog, Disclosure, Transition } from "@headlessui/react";
import { useRouter } from "next/router";
import { XMarkIcon as XIcon } from "@heroicons/react/24/outline";
import InfiniteScroll from "react-infinite-scroll-component";
import Link from "next/link";

import { projectDeliveryMethods, projectCategories } from "../lib/constants";
import { getAllProjects, getSettingsForPage } from "../lib/api";
import ProjectCard from "../components/project-card";
import Meta from "../components/meta";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function NextLink(props) {
  let { href, children, ...rest } = props;
  return (
    <Link href={href} shallow {...rest}>
      {children}
    </Link>
  );
}

const perPage = 30;
const LazyList = ({ data: initialData, page = 1, setPage }) => {
  if (!initialData || !initialData.length) return null;
  let data = [...initialData];

  if (data.length > perPage) {
    data = data.splice(0, page * perPage);
  }

  return (
    <InfiniteScroll
      className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-6 xl:grid-cols-3"
      dataLength={data.length} //This is important field to render the next data
      next={() => {
        setPage(page + 1);
      }}
      hasMore={data.length < initialData.length}
      loader={<h4>Loading...</h4>}
    >
      {data.map((product) => (
        <ProjectCard key={product["_id"]} data={product} />
      ))}
    </InfiniteScroll>
  );
};

const filterData = (data, filters) => {
  if (!data || !data.length) return [];

  data = [...data];
  const { methods, categories, amount, sort } = filters;

  if (methods.length) {
    data = data.filter((i) => methods.includes(i.method));
  }

  if (categories.length) {
    data = data.filter((i) => {
      const cats = i.categories;
      if (!cats || !cats.length) return false;
      return cats.findIndex((cat) => categories.includes(cat)) > -1;
    });
  }

  if (amount.length) {
    data = data.filter((i) => {
      const val = i.amount;
      let isValid = false;
      for (const range of amount) {
        const start = range.split("-")[0];
        const end = range.split("-")[1];
        if (val >= start && (!end || val <= end)) {
          isValid = true;
        }
      }
      return isValid;
    });
  }

  if (sort) {
    if (sort === "title") {
      data.sort((a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      });
    } else if (sort === "amountLow") {
      data.sort((a, b) => a.amount - b.amount);
    } else if (sort === "amountHigh") {
      data.sort((a, b) => b.amount - a.amount);
    }
  }

  return data;
};

export default function Projects({ data: initialData = [], settings }) {
  const router = useRouter();
  const { query } = router || {};
  const { methods = "", categories = "", amount = "", sort } = query || {};
  const [page, setPage] = useState(1);

  const activeFilters = {
    methods: methods ? methods.split(",") : [],
    categories: categories ? categories.split(",") : [],
    amount: amount ? amount.split(",") : [],
    sort,
  };
  const filtersActive = !!(
    activeFilters.methods.length ||
    activeFilters.categories.length ||
    activeFilters.amount.length
  );

  let activeFilterString = "";
  if (methods) activeFilterString = `?methods=${methods}`;
  if (categories) {
    const symbol = methods ? "&" : "?";
    activeFilterString = `${activeFilterString}${symbol}categories=${categories}`;
  }
  if (amount) {
    const symbol = methods ? "&" : "?";
    activeFilterString = `${activeFilterString}${symbol}amount=${amount}`;
  }

  const filters = [
    {
      id: "amount",
      name: "Budget",
      options: [
        {
          value: "0-1000000",
          label: "$0 - $1M",
          checked: activeFilters.amount.includes("0-1000000"),
        },
        {
          value: "1000000-10000000",
          label: "$1M - $10M",
          checked: activeFilters.amount.includes("1000000-10000000"),
        },
        {
          value: "10000000-25000000",
          label: "$10M - $25M",
          checked: activeFilters.amount.includes("10000000-25000000"),
        },
        {
          value: "25000000-50000000",
          label: "$25M - $50M",
          checked: activeFilters.amount.includes("25000000-50000000"),
        },
        {
          value: "50000000",
          label: "Over $50M",
          checked: activeFilters.amount.includes("50000000"),
        },
      ],
    },
    {
      id: "method",
      name: "Delivery Method",
      options: projectDeliveryMethods.map((i) => ({
        ...i,
        label: i.title,
        checked: activeFilters.methods.includes(i.value),
      })),
    },
    {
      id: "category",
      name: "Category",
      options: projectCategories.map((i) => ({
        ...i,
        label: i.title,
        checked: activeFilters.categories.includes(i.value),
      })),
    },
  ];
  const sortOptions = [
    {
      name: "Newest",
      href: filtersActive ? `/projects${activeFilterString}` : "/projects",
      current: !sort,
    },
    // {
    //   name: "Relevance",
    //   href: filtersActive ? `/projects${activeFilterString}` : "/projects",
    //   current: !sort,
    // },
    {
      name: "Amount: High-Low",
      href: filtersActive
        ? `/projects${activeFilterString}&sort=amountHigh`
        : "/projects?sort=amountHigh",
      current: sort === "amountHigh",
    },
    {
      name: "Amount: Low-High",
      href: filtersActive
        ? `/projects${activeFilterString}&sort=amountLow`
        : "/projects?sort=amountLow",
      current: sort === "amountLow",
    },
    {
      name: "Title: A-Z",
      href: filtersActive
        ? `/projects${activeFilterString}&sort=title`
        : "/projects?sort=title",
      current: sort === "title",
    },
  ];

  const data = filterData(initialData, activeFilters);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const handleFilterChanged = (e) => {
    const { value, checked, dataset } = e.target;
    const { id: type } = dataset || {};

    let methodsQueryParamsStr = methods;
    const methodsArr = methods ? methods.split(",") : [];
    if (type === "method") {
      if (!checked && methodsArr.includes(value)) {
        const index = methodsArr.findIndex((i) => i === value);
        methodsArr.splice(index, 1);
      } else if (checked && !methodsArr.includes(value)) {
        methodsArr.push(value);
      }
    }
    methodsQueryParamsStr = methodsArr.join(",");

    let categoriesQueryParamsStr = methods;
    const categoriesArr = categories ? categories.split(",") : [];
    if (type === "category") {
      if (!checked && categoriesArr.includes(value)) {
        const index = categoriesArr.findIndex((i) => i === value);
        categoriesArr.splice(index, 1);
      } else if (checked && !categoriesArr.includes(value)) {
        categoriesArr.push(value);
      }
    }
    categoriesQueryParamsStr = categoriesArr.join(",");

    let amountQueryParamsStr = methods;
    const amountArr = amount ? amount.split(",") : [];
    if (type === "amount") {
      if (!checked && amountArr.includes(value)) {
        const index = amountArr.findIndex((i) => i === value);
        amountArr.splice(index, 1);
      } else if (checked && !amountArr.includes(value)) {
        amountArr.push(value);
      }
    }
    amountQueryParamsStr = amountArr.join(",");

    let queryParamsStr = "";
    if (methodsQueryParamsStr) {
      queryParamsStr = `?methods=${methodsQueryParamsStr}`;
    }
    if (categoriesQueryParamsStr) {
      const symbol = methodsQueryParamsStr ? "&" : "?";
      queryParamsStr = `${queryParamsStr}${symbol}categories=${categoriesQueryParamsStr}`;
    }
    if (amountQueryParamsStr) {
      const symbol = methodsQueryParamsStr ? "&" : "?";
      queryParamsStr = `${queryParamsStr}${symbol}amount=${amountQueryParamsStr}`;
    }
    if (sort) {
      const symbol = queryParamsStr ? "&" : "?";
      queryParamsStr = `${queryParamsStr}${symbol}sort=${sort}`;
    }

    setPage(1);
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const isMobile = vw <= 1024;
    router.push(`/projects${queryParamsStr}`, undefined, {
      scroll: isMobile,
      shallow: true,
    });
    // Only scroll to content desktop & window has been scrolled
    if (!isMobile && window.scrollY > 75) {
      document.getElementById("projectFilters").scrollIntoView();
    }
  };

  return (
    <>
      <Meta path="/projects" {...settings.seo} />

      <div className="bg-white">
        <div>
          {/* Mobile filter dialog */}
          <Transition.Root show={mobileFiltersOpen} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 flex z-40 lg:hidden"
              onClose={setMobileFiltersOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>

              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-6 flex flex-col overflow-y-auto">
                  <div className="px-4 flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Filters
                    </h2>
                    <button
                      type="button"
                      className="-mr-2 w-10 h-10 p-2 flex items-center justify-center text-gray-400 hover:text-gray-500"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Filters */}
                  <form className="mt-4">
                    {filters.map((section) => (
                      <Disclosure as="div" key={section.name} className="">
                        {({ open }) => (
                          <fieldset>
                            <legend className="w-full px-2">
                              <Disclosure.Button className="w-full pt-4 pb-4 p-2 flex items-center justify-between text-gray-400 hover:text-gray-500">
                                <span className="text-sm font-medium text-gray-900">
                                  {section.name}
                                </span>
                                <span className="ml-6 h-7 flex items-center">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? "-rotate-180" : "rotate-0",
                                      "h-5 w-5 transform"
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                              </Disclosure.Button>
                            </legend>
                            <Disclosure.Panel className="pt-4 pb-2 px-4">
                              <div className="space-y-6">
                                {section.options.map((option, optionIdx) => (
                                  <div
                                    key={option.value}
                                    className="flex items-center"
                                  >
                                    <input
                                      id={`${section.id}-${optionIdx}-mobile`}
                                      data-id={section.id}
                                      name={`${section.id}[]`}
                                      defaultValue={option.value}
                                      defaultChecked={option.checked}
                                      type="checkbox"
                                      onInput={handleFilterChanged}
                                      className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                                    />
                                    <label
                                      htmlFor={`${section.id}-${optionIdx}-mobile`}
                                      className="ml-3 text-sm text-gray-500"
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </fieldset>
                        )}
                      </Disclosure>
                    ))}
                  </form>
                </div>
              </Transition.Child>
            </Dialog>
          </Transition.Root>

          <main className="max-w-2xl mx-auto px-4 lg:max-w-7xl lg:px-8">
            <div className="relative z-10 flex items-baseline justify-between pt-10">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
                Projects
              </h1>

              <div className="flex items-center">
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                      Sort
                      <ChevronDownIcon
                        className="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {sortOptions.map((option) => (
                          <Menu.Item key={option.name}>
                            {({ active }) => (
                              <NextLink
                                href={option.href}
                                className={classNames(
                                  option.current
                                    ? "font-medium text-gray-900"
                                    : "text-gray-500",
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm"
                                )}
                              >
                                {option.name}
                              </NextLink>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                <button
                  type="button"
                  className="inline-flex items-center lg:hidden ml-3"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <span className="text-sm font-medium text-gray-700">
                    Filters
                  </span>
                  <PlusSmIcon
                    className={`flex-shrink-0 ml-1 h-5 w-5 ${
                      filtersActive ? "text-accent" : "text-gray-400"
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            <div
              className="lg:mt-4 lg:pt-4 pb-24 lg:grid lg:grid-cols-3 lg:gap-x-8 xl:grid-cols-4"
              id="projectFilters"
            >
              <aside>
                <h2 className="sr-only">Filters</h2>

                <div className="hidden pl-2.5 -ml-2.5 sticky top-[calc(64px+1.25rem)] pt-5 -mt-5 lg:block overflow-y-auto max-h-[calc(100vh-64px-1.25rem)]">
                  <form className="space-y-10" key={activeFilterString}>
                    {filters.map(
                      (
                        section
                        // sectionIdx
                      ) => (
                        <div
                          key={section.name}
                          // className={sectionIdx === 0 ? null : "pt-6"}
                        >
                          <fieldset>
                            <legend className="block text-sm font-semibold text-gray-900">
                              {section.name}
                            </legend>
                            <div className="pt-5 space-y-3">
                              {section.options.map((option, optionIdx) => (
                                <div
                                  key={option.value}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`${section.id}-${optionIdx}`}
                                    data-id={section.id}
                                    name={`${section.id}[]`}
                                    defaultValue={option.value}
                                    defaultChecked={option.checked}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        e.target.click();
                                      }
                                    }}
                                    type="checkbox"
                                    onInput={handleFilterChanged}
                                    className="h-4 w-4 border-gray-300 rounded text-accent-600 focus:ring-accent-500"
                                  />
                                  <label
                                    htmlFor={`${section.id}-${optionIdx}`}
                                    className="ml-3 text-sm text-gray-600"
                                  >
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </fieldset>
                        </div>
                      )
                    )}
                  </form>
                </div>
              </aside>

              <section
                aria-labelledby="product-heading"
                className="mt-6 lg:mt-0 lg:col-span-2 xl:col-span-3"
              >
                <h2 id="product-heading" className="sr-only">
                  Products
                </h2>

                <LazyList data={data} setPage={setPage} page={page} />
              </section>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ preview = false }) {
  const data = await getAllProjects(preview);
  const settings = await getSettingsForPage("projects");

  return {
    props: { data, settings },
    revalidate: 10,
  };
}
